import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Container,
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { getCsrfToken } from '../utils/cookies';
import { cardLgSx, fieldSx, primaryButtonSx } from './pvtaStyles';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const ResetPassword: React.FC = () => {
    const query = useQuery();
    const uid = query.get('uid') || '';
    const token = query.get('token') || '';
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Beispielhafte Richtlinien: mind. 8 Zeichen, mindestens ein Großbuchstabe, ein Kleinbuchstabe, eine Ziffer und ein Sonderzeichen
    const validatePassword = (password: string): string | null => {
        const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/;
        if (!regex.test(password)) {
            return 'Das Passwort muss mind. 8 Zeichen lang sein und Groß-/Kleinbuchstaben, Ziffern und Sonderzeichen enthalten.';
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setMessage('');
        setPasswordError('');

        // Überprüfen, ob beide Passwörter übereinstimmen
        if (newPassword !== confirmPassword) {
            setPasswordError('Die Passwörter stimmen nicht überein.');
            return;
        }
        // Passwort validieren
        const validationError = validatePassword(newPassword);
        if (validationError) {
            setPasswordError(validationError);
            return;
        }

        const csrfToken = getCsrfToken();
        const response = await fetch('/api/password-reset-confirm/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            body: JSON.stringify({ uid, token, new_password: newPassword }),
        });
        const data = await response.json();
        setMessage(data.detail);
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                background:
                    'linear-gradient(160deg, var(--pvta-bg) 0%, var(--pvta-green-100) 100%)',
            }}
        >
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center', px: { xs: 0, sm: 2 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        ...cardLgSx,
                        p: { xs: 3, sm: 4 },
                        width: '100%',
                        maxWidth: 420,
                        boxSizing: 'border-box',
                    }}
                >
                    <Box display="flex" justifyContent="center" mb={2}>
                        <img
                            src="/Wappen_PVTA.png"
                            alt="Logo"
                            style={{ maxWidth: '100%', height: 'auto' }}
                        />
                    </Box>
                    <Typography
                        align="center"
                        sx={{
                            fontFamily: 'Georgia, "Times New Roman", serif',
                            fontWeight: 700,
                            fontSize: 26,
                            color: 'var(--pvta-green-900)',
                            mb: 0.5,
                        }}
                    >
                        Neues Passwort setzen
                    </Typography>
                    <Typography
                        variant="body2"
                        align="center"
                        sx={{ color: 'var(--pvta-muted)', mb: 2 }}
                    >
                        Vergib ein neues, sicheres Passwort für dein Konto.
                    </Typography>
                    <form onSubmit={handleSubmit}>
                        <TextField
                            label="Neues Passwort"
                            type={showPassword ? 'text' : 'password'}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            size="small"
                            autoComplete="new-password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            helperText="Mind. 8 Zeichen, Groß-/Kleinbuchstaben, Zahl & Sonderzeichen"
                            sx={{ ...fieldSx }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            label="Passwort bestätigen"
                            type={showConfirmPassword ? 'text' : 'password'}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            size="small"
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                            error={!!passwordError}
                            helperText={passwordError}
                            sx={{ ...fieldSx }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            sx={{ ...primaryButtonSx, mt: 2 }}
                        >
                            Passwort ändern
                        </Button>
                        {message && (
                            <Typography
                                variant="body2"
                                align="center"
                                color="textSecondary"
                                sx={{ mt: 2 }}
                            >
                                {message}
                            </Typography>
                        )}
                    </form>
                </Paper>
            </Container>
        </Box>
    );
};

export default ResetPassword;
